import { useDispatch } from "react-redux"
import { DefaultError, GetNextPageParamFunction, GetPreviousPageParamFunction, InfiniteData, QueryFunctionContext, useInfiniteQuery, useQuery, UseInfiniteQueryOptions, UseQueryOptions } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { convertTanStackStatus, OK, ResourceResult } from "helpers/data-statuses"
import { noop } from "lodash"

export function useGetQuery<T = unknown>({ url, key, ...otherOptions }: { url: string, key?: unknown[] } & Omit<UseQueryOptions<T>, "queryFn" | "queryKey">) {
  const dispatch = useDispatch()

  return useQuery<T>({
    ...otherOptions,
    queryKey: key || [url],
    queryFn: () => new Promise((resolve, reject) => {
      dispatch(Creators.get(url, resolve, reject))
    }),
  })
}

export function useInfiniteGetQuery<TQueryFnData = unknown, TPageParam = string | null>({
  url,
  key,
  enabled = true,
  getPreviousPageParam,
  getNextPageParam,
  initialPageParam,
  staleTime,
  refetchOnWindowFocus,
}: {
  url: string
  key?: string[]
  enabled?: boolean
  getPreviousPageParam: GetPreviousPageParamFunction<TPageParam, TQueryFnData>
  getNextPageParam: GetNextPageParamFunction<TPageParam, TQueryFnData>
  initialPageParam: TPageParam
  staleTime?: number
  refetchOnWindowFocus?: UseInfiniteQueryOptions<TQueryFnData, DefaultError, InfiniteData<TQueryFnData>, string[], TPageParam>["refetchOnWindowFocus"]
}) {
  const dispatch = useDispatch()

  return useInfiniteQuery<TQueryFnData, DefaultError, InfiniteData<TQueryFnData>, string[], TPageParam>({
    queryKey: key || [url],
    enabled,
    getNextPageParam,
    getPreviousPageParam,
    initialPageParam,
    staleTime,
    refetchOnWindowFocus,
    queryFn: ({ pageParam }: QueryFunctionContext<string[], TPageParam>) => new Promise((resolve, reject) => {
      const splitUrl = url.split("?", 2)
      let searchParams = new URLSearchParams()
      if (splitUrl.length > 1) {
        searchParams = new URLSearchParams(splitUrl[1])
      }
      if (pageParam) {
        searchParams.set("cursor", pageParam.toString())
      }
      dispatch(Creators.get(`${splitUrl[0]}?${searchParams.toString()}`, resolve, reject))
    }),
  })
}

// This is a weird pattern, because this is not a mutation, it's a query through a post.
// Not sure if this is re-usable outside of preferences
export function usePostQuery<TQueryFnData = unknown, TFnData = unknown>(
  { url, payload, key, enabled = true, select }: { url: string, payload: object, key?: string, enabled?: boolean, select: (data: TQueryFnData) => TFnData },
) {
  const dispatch = useDispatch()

  return useQuery<TQueryFnData, Error, TFnData>({
    queryKey: [key || url],
    enabled,
    queryFn: () => new Promise((resolve, reject) => {
      dispatch(Creators.post(url, payload, resolve, reject))
    }),
    select,
  })
}

export function useNullableQuery<TQueryFnData = unknown, TFnData = unknown>(query: UseQueryOptions<TQueryFnData, Error, TFnData> | null): ResourceResult<TFnData> {
  if (query === null) {
    query = {
      queryKey: ["disabled"],
      queryFn: noop as never,
      enabled: false,
    }
  } else {
    query = {
      ...query,
      // We need to be true if it was false before for the query to go through
      enabled: true,
    }
  }

  const result = useQuery(query)
  const dataStatus = convertTanStackStatus(result)
  if (dataStatus !== OK) {
    return [null, dataStatus]
  }

  // result.data can't be undefined at this point, but Typescript doesn't know that.
  return [result.data as TFnData, dataStatus]
}
